import React from 'react'
import App from 'next/app'
import { DefaultSeo } from 'next-seo'
import NProgress from '~/components/NProgress'
import Router from 'next/router'
import FacebookProvider from 'react-facebook/dist/FacebookProvider'
import { ToastProvider } from '@ticketswap/solar'
import BaseStyles from '../components/BaseStyles'
import {
  setTrackingIdentity,
  page,
  pageSource,
  trackExperiments,
} from '~/utils'
import { LiveChatProvider } from '../components/LiveChatContext'
import ViewerProvider from '../components/ViewerContext'
import ConsentDialog from '../components/ConsentDialog/dynamic'
import NetworkStatus from '../components/NetworkStatus'
import { captureException } from '../lib/sentry'
import createSeoConfig from '../lib/next-seo-defaults'
import { withTranslation, appWithTranslation } from '~/i18n'
import { withServerResponse } from '../components/ServerResponseContext'
import getConfig from 'next/config'
import LocaleBar from '../components/LocaleBar/dynamic'
import getViewer from '~/lib/get-viewer'
import { NativeBridge } from '../utils/nativeBridge'
import { LanguageProvider } from '~/components/LanguageContext'
import getLanguageFromHostname from '../utils/i18n/getLanguageFromHostname'
import getDeviceInfo from '~/utils/getDeviceInfo'
import { decodeGlobalId } from '~/utils/id'
import { withApollo } from '../lib/apollo'
import VoucherToast from '~/components/VoucherToast/dynamic'
import CookieToast from '~/components/CookieToast/dynamic'

const { FACEBOOK_APP_ID, USER_ID_COOKIE_NAME } = getConfig().publicRuntimeConfig

Router.events.on('routeChangeComplete', path => {
  // Track subsequent page-views
  page(path)
  // Trigger & track Google Optimize experiments after client-side
  // navigation.
  trackExperiments()
})

class MyApp extends App {
  static async getInitialProps({ Component, ctx }) {
    const deviceInfo = getDeviceInfo(ctx.req) || {}
    const viewer = await getViewer(ctx.apolloClient, ctx.req)

    if (viewer && ctx.res) {
      ctx.res.cookie(USER_ID_COOKIE_NAME, decodeGlobalId(viewer.id))
    }

    // Initiate vars to return
    let pageProps = { namespacesRequired: ['common'] }

    // Recompile pre-existing pageProps
    try {
      if (Component.getInitialProps) {
        pageProps = await Component.getInitialProps(ctx)
      }
    } catch (e) {
      console.log('------Error on Component.getInitialProps', e, ctx?.req?.path)
      captureException(e, null, ctx)
      throw e // We can also skip re-throwing and set property on pageProps
    }

    // `pageProps` will get serialized automatically by NextJs
    return {
      viewer,
      deviceInfo,
      pageProps,
      languageFromRequest: ctx?.req
        ? getLanguageFromHostname(ctx.req.headers.host)
        : null,
    }
  }

  componentDidMount() {
    // This will fix the native browser back-button: When user uses
    // the back navigation of the browser and goes from Sirius to
    // Website it results in the next/router not being able to
    // navigate back because the url is not in memory of the router.
    // Therefore we should just reload the page instead of using
    // next/router to navigate.

    // TODO: remove this when all routes are handled by Sirius.
    Router.beforePopState(({ url, as }) => {
      if (!url && !as) {
        // Reload page if page to go back to is a Website page.
        window.location.reload(false)
        return false
      }

      return true
    })

    const { viewer } = this.props
    viewer && setTrackingIdentity(viewer, false)

    // Track initial page-view
    page(Router.router.asPath)

    // Track referrer-info once per session
    pageSource(Router.router.asPath)

    // Trigger & track Google Optimize experiments on initial page-load
    trackExperiments(true)
  }

  // This reports errors thrown while rendering components
  componentDidCatch(error, errorInfo) {
    console.log('------ Component did catch on _app.js')
    captureException(error, null, { extra: errorInfo })
    super.componentDidCatch(error, errorInfo)
  }

  render() {
    const { Component, viewer, deviceInfo, pageProps, t } = this.props
    const seoDescription = t('common:site.description')
    const locale =
      this.props.languageFromRequest || this.props.i18n.language || 'en'
    const language = locale.split('-')[0]

    return (
      <LanguageProvider value={language}>
        <NProgress />
        <BaseStyles />
        <NativeBridge />
        <DefaultSeo {...createSeoConfig(seoDescription)} />
        <FacebookProvider appId={FACEBOOK_APP_ID} wait>
          <ViewerProvider data={{ viewer, deviceInfo }}>
            <LiveChatProvider>
              <ToastProvider>
                <VoucherToast t={t} />
                <ConsentDialog />
                <LocaleBar language={language} />
                <NetworkStatus />
                <Component {...pageProps} />
                {!deviceInfo.isApp && <CookieToast t={t} />}
              </ToastProvider>
            </LiveChatProvider>
          </ViewerProvider>
        </FacebookProvider>
      </LanguageProvider>
    )
  }
}

export default withApollo({ ssr: true })(
  appWithTranslation(withServerResponse(withTranslation(['common'])(MyApp)))
)
